// @file Functionality of drop_to_post_mixin

import { trackEvent } from '@@/bits/analytics'
import { isUrl } from '@@/bits/url'
import { useSurfaceMapStore } from '@@/pinia/surface_map'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import type { UseDropZoneOptions } from '@vueuse/core'
import { useDropZone } from '@vueuse/core'
import type { Ref } from 'vue'
import { computed, ref } from 'vue'

// TODO: remove this class and use state + TW Classes
// src of style: https://github.com/padlet/mozart/blob/e88326924444c4033e0e818e85c2d52da1e04862/services/rails/app/javascript/styles/3/surface/show/container.scss#L37-L41
const DRAGOVER_CLASS = 'is-dragover'

/**
 * Enables the ability to drop files or other draggable items onto a drop zone to create a new post.
 *
 * @param element - The HTML element that will serve as the drop zone.
 * @param dataTypes - An optional array of data types that the drop zone will accept.
 * @returns An object containing functions to enable and disable the drop-to-post functionality.
 */
export const useDragAndDropToCreatePost = (
  element: HTMLElement,
  dataTypes?: UseDropZoneOptions['dataTypes'],
): {
  isOverDropZone: Ref<boolean>
  dropToPostSubscribed: Ref<boolean>
  hasStartedDraggingOnThisComponent: Ref<boolean>
  enableDropToPost: (onFileDrop?: Function) => void
  disableDropToPost: () => void
} => {
  const surfaceMapStore = useSurfaceMapStore()
  const surfacePostsStore = useSurfacePostsStore()

  const dropZoneElement = computed(() => element ?? document.querySelector('#wall-container'))

  let onFileDropCallback: Function | null
  const dropToPostSubscribed = ref(false)

  /** We started seeing users selecting text from posts AND UI elements on the padlet
   * and dropping them onto the same padlet. These dragged texts would then become a post, leading to a lot
   * of confusion because they would contain weird text like "Avatar of X".
   *
   * By detecting and toggling this flag to true, we can block creation of posts
   * from text that has been dragged from on the padlet itself.
   * Dragging text from off the padlet onto the padlet will still work.
   *
   * Detection happens on the `dragstart` event when dropToPost is enabled.
   */
  const hasStartedDraggingOnThisComponent = ref(false)

  /**
   * Enables the ability to drop files or other draggable items onto a drop zone to create a new post.
   *
   * @param onFileDrop - A callback function that will be called when a file or other draggable item is dropped onto the drop zone. The callback function will receive the dropped content, the top and left coordinates of the drop, and the field name associated with the drop zone.
   */
  const enableDropToPost = (onFileDrop?: Function): void => {
    if (dropToPostSubscribed.value) return

    onFileDropCallback = onFileDrop ?? null
    dropToPostSubscribed.value = true
  }

  /**
   * Disables the ability to drop files or other draggable items onto the post creation area.
   * This is typically called when the user has finished creating a new post, to prevent further drops.
   */
  const disableDropToPost = (): void => {
    onFileDropCallback = null
    dropToPostSubscribed.value = false
  }

  const onFileDrop = (content: string | File, top: number, left: number, field: string): void => {
    trackEvent('Posts', 'add', `${field} drop`)
    const newPost = {
      attributes: {
        [field.toLowerCase()]: content,
        top,
        left,
      },
    }
    onFileDropCallback != null ? onFileDropCallback(newPost) : surfacePostsStore.createPostImmediately(newPost)
  }

  /**
   * Handles the drop event when a file or other draggable item is dropped onto the drop zone.
   *
   * @param files - An array of files that were dropped, or null if no files were dropped.
   * @param e - The DragEvent object representing the drop event.
   */
  const onDrop = (files: File[] | null, e: DragEvent): void => {
    if (!dropToPostSubscribed.value) return

    dropZoneElement.value.classList.remove(DRAGOVER_CLASS)

    e.preventDefault()
    const wishListElement = document.getElementById('wish-list')
    let left = 0
    let top = 0
    if (wishListElement != null) {
      left = Math.max(e.pageX - wishListElement.getBoundingClientRect().left, 0)
      top = Math.max(e.pageY - wishListElement.getBoundingClientRect().top, 0)
    } else {
      left = Math.max(e.pageX, 0)
      top = Math.max(e.pageY, 0)
    }

    let offset = 0

    if (e.dataTransfer != null) {
      if (files != null && files.length > 0) {
        for (let i = 0; i < files.length; i++) {
          const file = files[i]
          onFileDrop(file, top + offset, left + offset, 'File')
          offset += 8
        }
      } else {
        // a non-file has been likely dragged
        const link = e.dataTransfer.getData('text/uri-list')
        if (link !== '') {
          onFileDrop(link, top, left, 'link')
        } else {
          const text = e.dataTransfer.getData('text/plain')
          if (text !== '') {
            if (isUrl(text)) {
              onFileDrop(text, top, left, 'Link')
            } else {
              // post only if the text is not dragged from the dropzone.
              if (!hasStartedDraggingOnThisComponent.value) {
                onFileDrop(text, top, left, 'Body')
              }
            }
          }
        }
      }
    }

    // unset flag that indicates that a user has started
    // dragging on this page.
    hasStartedDraggingOnThisComponent.value = false
  }

  /**
   * Handles the 'dragenter' event when a draggable element enters the drop zone.
   * This function is responsible for adding the 'dragover' class to the drop zone
   * element to visually indicate that the drop zone is active.
   *
   * This function sets the flag that indicates a user has started dragging some kind of UI element
   * on the page.
   * We detect text (wish title/body), textarea (comment body), and img (avatars).
   * Note: that the reordering of posts also trigger a dragstart event but because
   * we've namespaced the event and its handler, it doesn't affect ordering of
   * posts.
   *
   * TODO: Check on this
   * 'dragstart' is not triggered when the user is dragging items
   * from outside the page, e.g. another app, or another browser.
   *
   * @param files - An array of files being dragged, or null if no files are being dragged.
   * @param e - The DragEvent object.
   */
  const onEnter = (_files: File[] | null, e: DragEvent): void => {
    if (!dropToPostSubscribed.value) return

    const node = e.target as HTMLElement
    if (node == null) return
    const nodeType = node.nodeName.toLowerCase()
    if (nodeType === '#text' || nodeType === 'textarea' || nodeType === 'img') {
      // Set flag that indicates a user has started dragging some kind of UI element
      // on the page.
      //
      // We detect text (wish title/body), textarea (comment body), and img (avatars).
      // Note: that the reordering of posts also trigger a dragstart event but because
      // we've namespaced the event and its handler, it doesn't affect ordering of
      // posts.
      hasStartedDraggingOnThisComponent.value = true
    }
  }

  /**
   * Handles the event when the drag and drop zone is exited.
   * @param _files - The files being dragged, if any.
   * @param e - The DragEvent object.
   */
  const onLeave = (_files: File[] | null, e: DragEvent): void => {
    if (!dropToPostSubscribed.value) return

    dropZoneElement.value.classList.remove(DRAGOVER_CLASS)
  }

  /**
   * Handles the 'dragover' event for a drop zone element.
   * This function checks if the drop zone is currently being dragged over, and if so, adds a CSS class to the drop zone element to indicate that it is being dragged over.
   * If the drag event is for a location picker pin, the function returns `true` to allow the event to be handled by the new drag and drop overlay and Vuex store.
   * Otherwise, the function returns `false` to prevent the default drag and drop behavior.
   *
   * @param _files - An array of files being dragged, or `null` if no files are being dragged.
   * @param e - The `DragEvent` object representing the 'dragover' event.
   * @returns `true` if the drag event is for a location picker pin, `false` otherwise.
   */
  const onOver = (_files: File[] | null, e: DragEvent): boolean => {
    if (!dropToPostSubscribed.value) return false

    if (!surfaceMapStore.isDraggingLocationPickerPin) {
      // let the drag event go through and handled by the new drag and drop overlay + vuexstore
      // in case a location picker pin (in map format) is dragged, still handle it using this vue composable for now
      return true
    }
    dropZoneElement.value.classList.add(DRAGOVER_CLASS)
    return false
  }

  const { isOverDropZone } = useDropZone(dropZoneElement, {
    onDrop,
    onEnter,
    onLeave,
    onOver,
    dataTypes,
  })

  return {
    isOverDropZone,
    dropToPostSubscribed,
    hasStartedDraggingOnThisComponent,
    enableDropToPost,
    disableDropToPost,
  }
}
